/**
 * ConsentBanner.jsx — the cookie choice, and the manager that acts on it.
 *
 * Shown until a choice is made (and again after 12 months, or when the
 * purposes change). Accept all is the filled primary button; Reject all sits
 * beside it at the same size, in the same row. "Manage preferences" opens one
 * switch per purpose, none pre-set. The same panel
 * reopens from any "Cookie settings" link. See lib/tracking.js for the rules.
 *
 * Also records the choice against a signed-in account, so there is a dated
 * record of what each person agreed to, and measures page views for anyone
 * who allowed analytics.
 *
 * Class names are cookiebar-*, not consent-*: onboarding already owns
 * .consent for its terms checkboxes, and the two sets of rules were landing on
 * each other — onboarding's `.consent input { width: 20px }` squeezed the
 * banner's switches until the knob slid out of its track.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Toggle } from './ui';
import {
  ANALYTICS_AVAILABLE, MARKETING_AVAILABLE, CONSENT_NEEDED, CONSENT_VERSION, GPC,
  getConsent, setConsent, needsConsentPrompt, onConsentChange, onOpenConsentSettings,
  openConsentSettings, applyConsent, trackPage,
} from '../lib/tracking';

const SYNC_KEY = 'leeflet.consent.synced';

export default function ConsentManager() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(() => needsConsentPrompt());
  const [choosing, setChoosing] = useState(false);
  const [draft, setDraft] = useState(() => ({
    analytics: getConsent()?.analytics ?? false,
    marketing: getConsent()?.marketing ?? false,
  }));

  // Every navigation: start whatever the choice allows, then count the page.
  useEffect(() => {
    applyConsent(pathname);
    trackPage(pathname);
  }, [pathname]);

  // A new choice takes effect on the page already open.
  useEffect(() => onConsentChange(() => {
    applyConsent(window.location.pathname);
    trackPage(window.location.pathname);
  }), []);

  // "Cookie settings" links anywhere reopen the panel at the switches.
  useEffect(() => onOpenConsentSettings(() => {
    const c = getConsent();
    setDraft({ analytics: c?.analytics ?? false, marketing: c?.marketing ?? false });
    setChoosing(true);
    setOpen(true);
  }), []);

  /**
   * The record against the account: what was chosen, when, under which
   * version. Written once per account per choice, not on every load.
   */
  const recordForAccount = useCallback((c) => {
    if (!user?.uid || !c) return;
    const marker = `${user.uid}:${c.at}`;
    try { if (window.localStorage.getItem(SYNC_KEY) === marker) return; } catch { /* ignore */ }
    setDoc(doc(db, 'users', user.uid, 'private', 'settings'), {
      cookieConsent: {
        analytics: c.analytics,
        marketing: c.marketing,
        version: CONSENT_VERSION,
        decidedAt: c.at,
        recordedAt: serverTimestamp(),
      },
    }, { merge: true })
      .then(() => { try { window.localStorage.setItem(SYNC_KEY, marker); } catch { /* ignore */ } })
      .catch(() => { /* retried on the next load */ });
  }, [user?.uid]);

  useEffect(() => { recordForAccount(getConsent()); }, [recordForAccount]);

  const decide = (choice) => {
    const c = setConsent(choice);
    recordForAccount(c);
    setOpen(false);
    setChoosing(false);
  };

  if (!CONSENT_NEEDED || !open) return null;

  return (
    <section className="cookiebar" role="dialog" aria-modal="false" aria-labelledby="cookiebar-title">
      <style dangerouslySetInnerHTML={{ __html: CONSENT_CSS }} />
      <div className="cookiebar__inner">
        <div className="cookiebar__icon" aria-hidden="true">
          <ShieldCheck size={22} />
        </div>

        <div className="cookiebar__body">
          <h2 id="cookiebar-title" className="cookiebar__title">Help us improve Clincordant</h2>
          <p className="cookiebar__text">
            {ANALYTICS_AVAILABLE && 'With your permission, we use analytics cookies to learn which features clinicians rely on, so we can build what matters to you. '}
            {MARKETING_AVAILABLE && 'On our public pages, marketing cookies tell us which adverts bring people here. '}
            We never receive your name, posts, messages or searches, and you can change your mind at any time.
          </p>
          <p className="cookiebar__meta">
            <Link to="/legal/cookies" onClick={() => setOpen(false)}>Cookie policy</Link>
            {!choosing && (
              <>
                <span aria-hidden="true">·</span>
                <button type="button" className="cookiebar__link" onClick={() => setChoosing(true)}>
                  Manage preferences
                </button>
              </>
            )}
          </p>

          {choosing && (
            <div className="cookiebar__choices">
              <div className="cookiebar__row cookiebar__row--fixed">
                <span>
                  <strong>Strictly necessary</strong>
                  <span className="cookiebar__desc">Signing in, security and your settings.</span>
                </span>
                <span className="cookiebar__always">Always on</span>
              </div>
              {ANALYTICS_AVAILABLE && (
                <div className="cookiebar__row">
                  <Toggle
                    label="Analytics"
                    description="Which pages and features are used. Google Analytics, with names and identifiers removed."
                    checked={draft.analytics}
                    onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))}
                  />
                </div>
              )}
              {MARKETING_AVAILABLE && (
                <div className="cookiebar__row">
                  <Toggle
                    label="Marketing"
                    description={GPC
                      ? 'Off: your browser has asked sites not to share or sell your data.'
                      : 'Measures our advertising, on public pages only. Never inside the app.'}
                    checked={draft.marketing && !GPC}
                    disabled={GPC}
                    onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/*
          Accept is the filled, primary button; Reject sits beside it at the
          same size and in the same place. That equal footing is what makes the
          consent valid under PECR (the ICO has written to sites whose reject
          option was smaller, hidden or a second click away) — and consent that
          is not valid makes the analytics data unusable.
        */}
        <div className="cookiebar__actions">
          {choosing ? (
            <>
              <button type="button" className="cookiebar__btn cookiebar__btn--secondary" onClick={() => decide({ analytics: false, marketing: false })}>
                Reject all
              </button>
              <button type="button" className="cookiebar__btn cookiebar__btn--primary" onClick={() => decide(draft)}>
                Save choices
              </button>
            </>
          ) : (
            <>
              <button type="button" className="cookiebar__btn cookiebar__btn--secondary" onClick={() => decide({ analytics: false, marketing: false })}>
                Reject all
              </button>
              <button type="button" className="cookiebar__btn cookiebar__btn--primary" onClick={() => decide({ analytics: true, marketing: true })}>
                Accept all
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * "Cookie settings" — reopens the choices panel. Rendered wherever the policy
 * promises a way back: the home page footer, the cookie policy and Settings.
 * Nothing renders when no optional cookies are configured.
 */
export function CookieSettingsLink({ className = 'link-btn', children = 'Cookie settings' }) {
  if (!CONSENT_NEEDED) return null;
  return (
    <button type="button" className={className} onClick={openConsentSettings}>
      {children}
    </button>
  );
}

export const CONSENT_CSS = `
.cookiebar {
  position: fixed; z-index: 900;
  left: 12px; right: 12px; bottom: calc(14px + env(safe-area-inset-bottom));
  max-width: 880px; margin: 0 auto;
  max-height: calc(100vh - 28px); overflow-y: auto;
  background: var(--card); color: var(--ink);
  border: 1px solid var(--line-strong); border-radius: 18px;
  box-shadow: 0 24px 60px -20px rgba(10, 20, 16, 0.45), 0 2px 6px rgba(10, 20, 16, 0.06);
  animation: cookiebar-in 320ms cubic-bezier(.2,.8,.2,1);
}
.cookiebar__inner {
  display: grid; grid-template-columns: auto 1fr auto; align-items: center;
  gap: 18px; padding: 20px 22px;
}
.cookiebar__icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: grid; place-items: center;
  background: var(--primary-tint); color: var(--primary-text);
  border: 1px solid color-mix(in srgb, var(--primary) 30%, var(--line-strong));
}
.cookiebar__title {
  margin: 0 0 4px; font-family: var(--font-display);
  font-size: var(--text-lg); font-weight: 600; letter-spacing: -0.01em;
}
.cookiebar__text { margin: 0; font-size: var(--text-sm); line-height: 1.55; color: var(--ink-soft); max-width: 62ch; }
.cookiebar__meta {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin: 8px 0 0; font-size: var(--text-xs); color: var(--ink-faint);
}
.cookiebar__meta a, .cookiebar__link {
  color: var(--primary-text); font-weight: 600; font-size: var(--text-xs);
  text-decoration: underline; text-underline-offset: 2px;
  background: none; border: 0; padding: 0; cursor: pointer; font-family: inherit;
}
.cookiebar__actions { display: flex; gap: 10px; align-items: center; }
.cookiebar__btn {
  min-width: 132px; min-height: 46px; padding: 0 20px;
  border-radius: 12px; font: inherit; font-size: var(--text-sm); font-weight: 700;
  cursor: pointer; transition: transform 140ms ease, box-shadow 140ms ease, background 140ms ease, border-color 140ms ease;
}
.cookiebar__btn--primary {
  background: var(--primary); color: var(--on-accent); border: 1.5px solid var(--primary);
  box-shadow: 0 8px 20px -8px color-mix(in srgb, var(--primary) 70%, transparent);
}
.cookiebar__btn--primary:hover { background: var(--primary-hover); border-color: var(--primary-hover); transform: translateY(-1px); box-shadow: 0 12px 26px -10px color-mix(in srgb, var(--primary) 80%, transparent); }
.cookiebar__btn--secondary { background: var(--card); color: var(--ink); border: 1.5px solid var(--line-ui); }
.cookiebar__btn--secondary:hover { border-color: var(--ink-soft); transform: translateY(-1px); }
.cookiebar__btn:active { transform: translateY(0) scale(0.98); }
.cookiebar__btn:focus-visible { outline: none; box-shadow: var(--focus-ring); }

.cookiebar__choices { display: grid; gap: 8px; margin: 14px 0 0; }
.cookiebar__row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 11px 14px; border: 1px solid var(--line-strong); border-radius: 12px;
  background: var(--bg-alt);
}
.cookiebar__row > .toggle-row { flex: 1; padding: 0; align-items: center; }
.cookiebar__row .toggle-row__desc { color: var(--ink-soft); }
.cookiebar__row strong { display: block; font-size: var(--text-sm); }
.cookiebar__desc { display: block; font-size: var(--text-xs); color: var(--ink-soft); line-height: 1.45; margin-top: 2px; }
.cookiebar__always { font-size: var(--text-xs); font-weight: 600; color: var(--ink-soft); white-space: nowrap; }

@media (max-width: 720px) {
  .cookiebar__inner { grid-template-columns: auto 1fr; padding: 18px; }
  .cookiebar__actions { grid-column: 1 / -1; }
  .cookiebar__btn { flex: 1; min-width: 0; }
}
@keyframes cookiebar-in { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .cookiebar { animation: none; } .cookiebar__btn { transition: none; } }
`;
